import type { FunctionalComponent, HTMLAttributes, VNodeProps } from 'vue';
import type { ClientAssignmentConfigRequest } from './_ClientAssignment';
import type { ClientDeliverySheet } from './_ClientDeliverySheet';
import type { UserRoleValues } from '@/config/user-roles';
import type { RolloutPlanValues } from '@/config/rollout-plans';
import type { ERROR_IMPORT_TYPES } from './error';
import type { DefaultClientTableColumnKey } from '~/stores/static/client-default-table-columns';
export * from './_Appointment';
export * from './_AppointmentTask';
export * from './_ClientFilter';
export * from './_ConditionButtons';
export * from './_ClientAssignment';
export * from './_ClientDeliverySheet';
export * from './_ClientFilter';
export * from './_DataTable';
export * from './_Reporting';
export * from './_Sort';
export * from './_UserRole';
export * from './_UserNotification';
export * from './_File';

export const REMAINING_CLIENTS = 'remainingClients';

export type Organization = {
  _id: string;
  name: string;
  stripeCustomerId?: string;
  settings?: {
    logoUrl?: string;
    replyTo?: string;
    imprintName?: string;
    imprintStreetAndNumber?: string;
    imprintCity?: string;
    imprintCountry?: string;
  };
  created: string;
  updated: string;
};

export type ClientTableColumn = {
  fieldId: CustomField['_id'] | DefaultClientTableColumnKey;
  order: number;
};

export type UserSettings = {
  disableSuccessNotifications?: boolean;
  enableDarkMode?: boolean;
  clientTableColumns?: {
    [rolloutId: Rollout['_id']]: ClientTableColumn[];
  };
  disableAssignToEmailNotification?: boolean;
  disableAssignToPushNotification?: boolean;
  disableUnAssignFromEmailNotification?: boolean;
  disableUnAssignFromPushNotification?: boolean;
  disableUpdateClientUserEmailNotification?: boolean;
  disableUpdateClientUserPushNotification?: boolean;
  disableCommentMentionedEmailNotification?: boolean;
  disableCommentMentionedPushNotification?: boolean;
  disableClientTaskCreateEmailNotification?: boolean;
  disableClientTaskCreatePushNotification?: boolean;
  disableAssignToTaskEmailNotification?: boolean;
  disableAssignToTaskPushNotification?: boolean;
  disableUnassignFromTaskEmailNotification?: boolean;
  disableUnassignFromTaskPushNotification?: boolean;
};

// FE
export type User = {
  _id: string;
  name: string;
  email: string;
  password?: string;
  role: UserRoleValues;
  picture?: string;
  rollouts: Rollout[];
  clients: Client[];
  orgId: Organization['_id'];
  created: string | Date;
  updated: string | Date;
  language?: Language;
  isDisabled?: boolean;
  settings?: UserSettings;
};

export type Language = 'en' | 'de';

export interface UserMin extends Omit<User, 'rollouts'> {}

export type Rollout = {
  _id: string;
  identifier: string;
  name: string;
  inventories?: Inventory[];
  image?: string;
  orgId: string;
  isActivated: boolean;
  created: string;
  updated: string;
  clientsDone: number;
  totalClients: number;
  techniciansAssigned: number;
  clientLimit: number;
  plan: RolloutPlanValues;
};

// FE
export interface CreateRollout extends Omit<Rollout, 'inventories'>, Document {
  inventoryIds: string[];
}

// FE
export type CustomFieldGroup = {
  _id: string;
  name: string;
  customFields: CustomField[];
};

// FE
export interface Inventory {
  _id: string;
  orgId: string;
  name: string;
  workflow: Workflow;
  isWorkflowDisabled?: boolean;
  rollout: string;
  customFieldGroups?: CustomFieldGroup[] | undefined;
  clientAmount?: number;
  scheduledChecklists?: Record<
    WorkflowStatus['_id'],
    Record<Checklist['_id'], TimelineSchedule>
  >;
  scheduledClientGroups?: Record<
    ClientGroup['_id'],
    Record<WorkflowStatus['_id'], TimelineSchedule>
  >;
  scheduledType?: RoadmapPlanningMethod;
  clientByStatus?: {
    status: WorkflowStatus;
    clientsAmount: number;
  }[];
  assignmentConfig?: ClientAssignmentConfigRequest;
  workingDays: number[];
}

// The client is an item of an inventory
// FE
export type Client = {
  _id: string;
  name: string;
  description?: string;
  inventory: Inventory;
  status: WorkflowStatus['_id'];
  clientGroups: ClientGroup[];
  assignee?: User;
  clientUser?: User;
  checklists: {
    [workflowStatusId: WorkflowStatus['_id']]: {
      [checklistId: Checklist['_id']]: {
        [checklistStepId: ChecklistStep['_id']]: {
          value: boolean;
          textInput: string;
        };
      };
    };
  };
  linkedClients: LinkedClient[];
  tasks?: ClientTask[];
  appointments?: ClientAppointmentsMap;
  comments: Comment[];
  created: Date;
  updated?: Date;
  rollout: Rollout['_id'];
  deliverySheet?: ClientDeliverySheet;
  flagged?: string;
};

export type ClientWithCustomFieldValues = Client & {
  customFieldValues?: CustomFieldValues;
};

export type PartialCustomField = {
  _id: CustomField['_id'];
  type: CustomFieldType;
};

export type CustomFieldWithValueRow = {
  customField: CustomField | PartialCustomField | undefined;
  value: string | string[] | undefined;
  uuid: string;
};

export type ClientAppointmentsMap = Record<string, ClientAppointment>;

export type ClientAppointment = {
  _id: string;
  appointment: string;
  done?: boolean;
  assignee?: User | string;
  date?: Date;
  comment?: string;
};

export type ClientAttachment = {
  _id: string;
  orgId: string;
  rolloutId: string;
  clientIds: string[];
  name: string;
  description?: string;
  size: number;
  url?: string;
  thumbnailUrl?: string;
  uploadedAt: string;
  uploadedBy: User;
};

export type DropdownControl = {
  value: string;
  name: string;
  disabled?: boolean;
  clickAction?: () => void;
};

export type MinifiedClient = Pick<
  Client,
  | '_id'
  | 'name'
  | 'description'
  | 'inventory'
  | 'status'
  | 'clientGroups'
  | 'assignee'
  | 'clientUser'
  | 'created'
>;

export type HistoryEntry = {
  _id: string;
  operation: ClientHistoryOperation;
  createdAt: string;
  createdBy: User;
  from: {
    value?: string | string[];
    checked?: boolean;
    checklistStep?: string;
    linkType?: string;
    customFieldName?: string;
    user?: User;
    statusIndex?: number;
    statusLength?: number;
    statusName?: string;
  };
  to: {
    value?: string | string[];
    checked?: boolean;
    checklistStep?: string;
    linkType?: string;
    customFieldName?: string;
    user?: User;
    statusIndex?: number;
    statusLength?: number;
    statusName?: string;
    flagReason?: string;
    attachmentName?: string;
    appointmentId?: string;
    appointmentName?: string;
  };
  client?: Client;
};

export type LinkedClient = {
  _id: string;
  destination: Client;
  clientLink: { _id: string; isAdvanced?: boolean };
  linkType: ClientLinkType;
};

export type ClientLink = {
  _id: string;
  orgId: string;
  inwards: string;
  outwards: string;
  isAdvanced?: boolean;
  sharedCustomFields?: { _id: string; name: string }[];
};

export type ClientLinkType = 'inwards' | 'outwards';

export type Comment = {
  _id: string;
  author: User;
  comment: string;
  createdAt: string;
  editedAt: string;
};

export type CustomColor =
  | 'slate'
  | 'gray'
  | 'stone'
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose';

// FE
export type ClientGroup = {
  _id: string;
  name: string;
  description: string;
  color: CustomColor;
  created: string;
  updated: string;
  orgId: string;
  rolloutId: string;
  order: number;
};

export type Priority = 'high' | 'medium' | 'low';

export type AppointmentTaskType = 'appointment' | 'task';

export type ClientTask = {
  _id: string;
  taskIdentifier: string;
  taskNumber: number;
  rollout: Rollout;
  assignee?: User;
  client: Client & { rollout?: Rollout };
  name: string;
  priority: Priority;
  description: string;
  date?: string;
  duration?: number;
  done: boolean;
  created: string;
  createdBy: User;
  updated: string;
  startTime: Date;
  endTime: Date;
  isAllDayTask?: boolean;
};

export type ClientDashboardTasks = Record<Client['_id'], ClientTask[]>;
export type ClientDashboardClientAppointments = Record<
  Client['_id'],
  ClientAppointment[]
>;

export type EditClientTask = {
  _id?: string;
  client?: {
    _id: string;
    name: string;
    rollout?: { _id: string } | string;
    assignee?: { name: string; _id: string; picture?: string };
    clientUser?: { name: string; _id: string; email: string };
  };
  assignee?: Partial<User>;
  startTime?: Date;
  endTime?: Date;
  minDate?: Date;
  maxDate?: Date;
  appointmentId?: string;
  type?: AppointmentTaskType;
  taskIdentifier?: string;
  duration: number;
  taskNumber?: number;
  rollout?: Partial<Rollout>;
  name: string;
  priority: Priority;
  description: string;
  date?: string;
  done?: boolean;
  isAllDayTask?: boolean;
  recipients?: string[];
  emailCustomField?: string;
  comment?: string;
};

export type CalendarClientTask = Omit<
  EditClientTask,
  'startTime' | 'endTime'
> & { startTime: Date; endTime: Date; dateFrom?: string; dateTo?: string };

export type CreateClientTask = Pick<
  ClientTask,
  'name' | 'description' | 'priority' | 'done' | 'date' | 'duration'
>;

export type CalendarView = 'day' | 'week' | 'month';

export type CalendarBadge = {
  type: 'start' | 'end';
  startDate: Date;
  endDate: Date;
  clientTask: ClientTask;
  columnSlot: number;
  parallelColumns: number;
  durationInMinutes: number;
};

export type CalendarQueryParams =
  | {
      date: DatepickerValue;
      view: CalendarView;
      assignee: 'all' | User['_id'];
    }
  | undefined;

export enum CustomFieldType {
  input = 'input',
  textarea = 'textarea',
  selection = 'selection',
  multiSelection = 'multi-selection',
  userField = 'user_field',
}

export const customFieldTypes = [
  CustomFieldType.input,
  CustomFieldType.textarea,
  CustomFieldType.selection,
  CustomFieldType.multiSelection,
  CustomFieldType.userField,
] as const;

export const unassignedOptionId = 'unassigned';

// Default inventory working days: Monday - Friday
export const defaultWorkingDays = [1, 2, 3, 4, 5];

// FE
export type CustomField = {
  _id: string;
  name: string;
  description: string;
  options?: string[];
  required?: boolean;
  hideOnDeliverySheet?: boolean;
  orgId: Organization['_id'];
  type: CustomFieldType;
  created: string;
  updated: string;
  readOnly?: boolean;
  value?: string | string[];
  regex?: string;
};

export type CustomFieldValues = Record<string, string | string[]>; // Record<customFieldId, value>

export type ClientCustomFieldValue = {
  clientId: Client['_id'];
  values?: CustomFieldValues | undefined;
};

export type MappingCustomFieldAdvancedLinks = Record<string, string>; // Record<customFieldId, advancedLink Inwards/outwards name>

export enum ClientDefaultFieldMode {
  IMPORT = 'IMPORT',
  EXPORT = 'EXPORT',
}

export enum ClientDefaultFieldId {
  Status = 'status',
  ClientFlag = 'client-flag',
  Assignee = 'assignee',
  ClientUser = 'client-user',
  Name = 'name',
  Description = 'description',
  ClientGroups = 'client-groups',
  ClientLinks = 'client-links',
}

export type ClientDefaultField = {
  _id: ClientDefaultFieldId;
  name: string;
  type?: CustomFieldType;
};

// FE
export type Workflow = {
  _id: string;
  name: string;
  statuses: WorkflowStatus[];
  orgId: Organization['_id'];
  created: string;
  updated: string;
  readOnly?: boolean;
};

export type CreateWorkflow = Omit<
  Workflow,
  '_id' | 'orgId' | 'created' | 'updated' | 'statuses'
> & { statuses: CreateWorkflowStatus[] };

// FE
export type WorkflowStatus = {
  _id: string;
  name: string;
  checklists: Checklist[];
};

export type CreateWorkflowStatus = Omit<WorkflowStatus, '_id'>;

// FE
export type WorkflowMin = {
  _id: string;
  name: string;
  statuses: WorkflowStatusMin[];
  orgId: Organization['_id'];
  created: number;
  updated: number;
};

export type WorkflowStatusMin = {
  _id: string;
  name: string;
  checklists: string[];
};

export type ChecklistConditionGroupCondition = 'not' | 'and' | 'or';

export type ChecklistConditionGroup = {
  condition: {
    [key in ChecklistConditionGroupCondition]: boolean;
  };
  rules: ChecklistConditionRule[];
  groups: ChecklistConditionGroup[];
};

export type ChecklistConditionRule =
  | {
      type: 'client-group';
      clientGroup: ClientGroup | ClientGroup['_id'] | null;
    }
  | {
      type: 'custom-field';
      customField: CustomField | CustomField['_id'] | null;
      compareValue: string | string[];
    };

export type Checklist = {
  _id: string;
  name: string;
  responsibleRoles: UserRoleValues[];
  isPersistent: boolean;
  orgId: string;
  steps: Omit<ChecklistStep, '_id' | 'checked'> &
    { _id?: string | undefined }[];
  conditions?: ChecklistConditionGroup;
};

export type ClientChecklist = Omit<Checklist, 'steps'> & ClientChecklistStep;

export type ChecklistStep = {
  _id: string;
  name: string;
  description: string;
  type: ChecklistStepType;
  customField?: CustomField;
  preselectedClientLink?: {
    clientLink: ClientLink;
    linkType: ClientLinkType;
  };
  checked?: boolean;
};

export type ChecklistStepType =
  | 'text-only'
  | 'text-input'
  | 'selection'
  | 'delivery'
  | 'custom-field'
  | 'client-name'
  | 'move-client'
  | 'link-client'
  | 'client-groups';

export type ClientChecklistStep = Checklist & Value;

export type Value = {
  value: unknown;
};

export enum InventoryExportFileFormat {
  CSV = 'CSV',
  XSLX = 'XSLX',
}

export type InventoryExportStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED';

export type InventoryExport = {
  _id: string;
  orgId: string;
  rolloutId: string;
  inventory: Inventory | null;
  exportedAt: string;
  exportedBy: string;
  fileUrl: string;
  format: string;
  exportedClientsAmount: number;
  exportedFieldsAmount: number;
  totalFieldsAmount: number;
  status: InventoryExportStatus;
  originalName: string;
};

export type InventoryImportStatus =
  | 'INCOMPLETE'
  | 'IN_APPROVAL'
  | 'DECLINED'
  | 'PROCESSING'
  | 'FAILED'
  | 'COMPLETE';

export type InventoryImportError = {
  errorHeader: ERROR_IMPORT_TYPES;
  column: string;
  row: number;
};

export interface SelectedFileData {
  name: string;
  size: number;
  fileUrl?: string;
}

export type SelectedFile = File | SelectedFileData;

export type InventoryImport = {
  _id: string;
  fieldname: string;
  originalname: string;
  encoding: string;
  mimetype: string;
  destination: string;
  filename: string;
  path: string;
  size: number;
  status: InventoryImportStatus;
  inventory: Inventory;
  fileHeader: string[];
  headersMapping: InventoryImportMapping[];
  errorMappingFields: InventoryImportError[];
  amountOfRows: number;
  createdBy: User;
  approvedBy?: User;
  updated?: string;
  fileUrl?: string;
  updateExistingClients?: boolean;
};

export type InventoryImportMapping = {
  column: string;
  field: CustomField | null;
  compareValue: any;
  createIfNotExists: boolean;
};

// FE ONLY
// FE
export type Icon = FunctionalComponent<
  HTMLAttributes & VNodeProps,
  Record<string, never>,
  any
>;

export type TableColumn = {
  key: string;
  name: string;
  clickable?: boolean;
};

// FE
export type TableColumns = TableColumn[];

export type WizardStep = {
  key: string;
  name: string;
  description?: string;
  isCompleted?: boolean;
};

export type WizardSteps = WizardStep[];

// Datepicker

export type DatepickerTime = {
  h?: number;
  m?: number;
};

export type DatepickerValue = {
  date?: Date;
  time?: DatepickerTime;
};

export type DatepickerRangeValue = {
  dateFrom?: Date;
  dateTo?: Date;
};

export type DatepickerDay = {
  date: Date;
  isOutsideCurrentMonth: boolean;
  isToday: boolean;
  isDisabled: boolean;
  isSelected?: boolean;
  isPipeLeft?: boolean;
  isPipeRight?: boolean;
};

export type DatepickerMode = 'date' | 'date-range' | 'date-time';

export type DatepickerTimeFormat = '24h' | '12h';

export type DatepickerColor = {
  date: Date;
  color: CustomColor;
};

export type DatepickerSelectedDate = {
  index: number;
  day?: DatepickerDay;
};

// Client History Actions

export enum ClientHistoryOperation {
  CREATE = 'CREATE',
  UPDATE_STATUS = 'UPDATE_STATUS',
  UPDATE_DESCRIPTION = 'UPDATE_DESCRIPTION',
  UPDATE_NAME = 'UPDATE_NAME',
  UPDATE_ASSIGNEE = 'UPDATE_ASSIGNEE',
  UPDATE_CLIENT_USER = 'UPDATE_CLIENT_USER',
  UPDATE_CLIENT_GROUPS = 'UPDATE_CLIENT_GROUPS',
  UPDATE_LINKED_CLIENTS = 'UPDATE_LINKED_CLIENTS',
  UPDATE_CUSTOM_FIELDS_VALUE = 'UPDATE_CUSTOM_FIELDS_VALUE',
  UPDATE_CHECKLIST_STEP_VALUE = 'UPDATE_CHECKLIST_STEP_VALUE',
  UPDATE_TASK = 'UPDATE_TASK',
  MOVE_CLIENT = 'MOVE_CLIENT',
  ADD_COMMENT = 'ADD_COMMENT',
  UPDATE_COMMENT = 'UPDATE_COMMENT',
  DELETE_COMMENT = 'DELETE_COMMENT',
  ADD_CLIENT_TASK = 'ADD_CLIENT_TASK',
  UPDATE_CLIENT_TASK = 'UPDATE_CLIENT_TASK',
  DELETE_CLIENT_TASK = 'DELETE_CLIENT_TASK',
  SIGN_DELIVERY_SHEET = 'SIGN_DELIVERY_SHEET',
  CLIENT_IS_FLAGGED = 'CLIENT_IS_FLAGGED',
  ATTACHMENT_UPLOADED = 'ATTACHMENT_UPLOADED',
  ATTACHMENT_DELETED = 'ATTACHMENT_DELETED',
  CONFIRMATION_EMAIL_SENT = 'CONFIRMATION_EMAIL_SENT',
  INVITATION_EMAIL_SENT = 'INVITATION_EMAIL_SENT',
}

// FE
export type AlertTypes =
  | 'success'
  | 'information'
  | 'warning'
  | 'error'
  | 'flagged';

export type TimelineRowChild = {
  _id: string;
  name: string;
  color?: CustomColor;
  schedules: TimelineSchedule[];
  statuses: TimelineStatus[];
};

export type TimelineStatus = { _id: string; name?: string };

export type TimelineRow = {
  name: string;
  inventoryId: string;
  color?: CustomColor;
  children?: TimelineRowChild[];
};

export type TimelineSchedule = {
  startDate: string; // UTC ISOString
  endDate: string; // UTC ISOString
  statusId?: string;
  displayName?: string;
};

export type TimelineScheduleUpdate = {
  schedule: TimelineSchedule;
  child: TimelineRowChild;
  inventoryId: string;
};

export type TimelineMonth = { month: number; year: number };

export type RoadmapPlanningMethod = 'checklist' | 'clientGroup';

export enum INVENTORY_SCHEDULED_TYPE {
  checklist = 'checklist',
  clientGroup = 'clientGroup',
}

export type ScheduleBadge = {
  id: string;
  statusId?: string;
  startDate: Date;
  endDate: Date;
  timespanLeft: number;
  displayName?: string;
  minStartDate?: Date;
  maxEndDate?: Date;
};

export type MoveDuplicateType = 'duplicate' | 'move';

export type MoveDuplicateKeepValue =
  | 'keepCustomFields'
  | 'keepClientTasks'
  | 'keepClientLink'
  | 'keepAssignee'
  | 'keepClientUser'
  | 'keepComments'
  | 'keepHistory'
  | 'keepDescription';

export type MoveDuplicateKeepValues = Record<MoveDuplicateKeepValue, boolean>;

export enum DATABASE_AUTOCOMPLETE_COLLECTION {
  Client = 'client',
  User = 'user',
  Inventory = 'inventory',
  ClientTask = 'clientTask',
  ClientGroup = 'clientGroup',
  Rollout = 'rollout',
  Checklist = 'checklist',
  ClientLink = 'clientLink',
  CustomField = 'customField',
  Workflow = 'workflow',
  ClientFilter = 'clientFilter',
}

export type PopoverPosition = 'left' | 'right';

export type DurationOption = { key: number; name: string };
